import { Linesheet, Sortable as SortableTypes } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { FormGrid } from '../Form';
import { AddCircleOutline, Remove } from '../Icons';
import { SectionCard } from '../SectionCard';
import { Sortable } from '../Sortable';

interface Props {
  allAttributes: Linesheet.DictionaryItem[];
  attributeIds: string[];
  onChange: (attributeIds: string[]) => void;
}

export const ProductAttributesSortList = ({ allAttributes, attributeIds, onChange }: Props) => {
  const { t } = useTranslation(['linesheets', 'common']);

  const availableAttributes = allAttributes
    .map(attribute => attribute.key)
    .filter(key => !attributeIds.includes(key))
    .map(key => key);

  const getAttributeName = React.useCallback(
    (key: string) => {
      return allAttributes.find(attr => attr.key === key)?.value ?? '';
    },
    [allAttributes],
  );

  const handleAddAttribute = React.useCallback(
    (attribute: string) => () => {
      onChange([...attributeIds, attribute]);
    },
    [attributeIds, onChange],
  );

  const handleAddAllAttributes = React.useCallback(() => {
    onChange([...attributeIds, ...availableAttributes]);
  }, [attributeIds, availableAttributes, onChange]);

  const renderAvailableAttributes = React.useCallback(
    (key: string) => {
      const attributeName = getAttributeName(key);

      return (
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t('common:add_value', { value: attributeName })}
            className="rounded-full absolute-cursor-pointer"
            onClick={handleAddAttribute(key)}
          >
            <AddCircleOutline className="size-4" />
          </button>
          <span className="body-4">{attributeName}</span>
        </div>
      );
    },
    [getAttributeName, handleAddAttribute, t],
  );

  const handleRemoveAttribute = React.useCallback(
    (attribute: string) => () => {
      const newAttributes = attributeIds.filter(addedAttribute => addedAttribute !== attribute);

      onChange(newAttributes);
    },
    [attributeIds, onChange],
  );

  const renderAddedAttributes = React.useCallback(
    (key: string) => {
      const name = getAttributeName(key);

      return (
        <div className="group/attribute flex w-full items-center justify-between gap-2">
          <span className="body-4">{name}</span>
          <Button
            aria-label={t('common:remove_value', { name })}
            icon={Remove}
            variant="text"
            color="danger"
            className="opacity-0 group-focus-within/attribute:opacity-100 group-hover/attribute:opacity-100 touch:opacity-100"
            dataTestId={`remove${name}`}
            onClick={handleRemoveAttribute(key)}
          />
        </div>
      );
    },
    [getAttributeName, handleRemoveAttribute, t],
  );

  const handleSortEnd = React.useCallback(
    (sortedArray: SortableTypes.Items<string>) => {
      onChange(sortedArray.map(item => item.id));
    },
    [onChange],
  );

  return (
    <FormGrid cols={2}>
      <SectionCard>
        <div className="mb-5 flex items-center justify-between gap-4">
          <h3 className="heading-3">{t('linesheets:product_attributes')}</h3>
          <Button size="medium" variant="bordered" color="secondary" onClick={handleAddAllAttributes}>
            {t('common:add_all')}
          </Button>
        </div>
        <div className="max-h-96 min-h-16 overflow-y-auto">
          <Sortable isDisabled items={availableAttributes.map(id => ({ id }))} renderItem={renderAvailableAttributes} />
        </div>
      </SectionCard>
      <SectionCard>
        <h3 className="mb-5 heading-3">{t('linesheets:added_attributes')}</h3>
        <div className="max-h-96 min-h-16 overflow-y-auto">
          <Sortable
            items={attributeIds.map(id => ({ id }))}
            renderItem={renderAddedAttributes}
            dragMode="handle"
            onSortEnd={handleSortEnd}
          />
        </div>
      </SectionCard>
    </FormGrid>
  );
};
